import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import Tooltip from 'rc-tooltip';
import { useNavigate } from 'react-router';

import { EngagementCreationPath } from '@/__generated__/graphql';
import RequestConsultationButton from '@/components/Button/RequestConsultationButton';
import Divider from '@/components/Divider';
import SendMessageButton from '@/components/SendMessageButton';
import V2Tooltip from '@/componentsv2/Tooltip';
import { Viewer } from '@/core/viewer';
import HireExpertButton from '@/features/consulting-engagements/live/HireExpertButton';
import { useRateDisplay } from '@/hooks/useRateDisplay';
import { darkGray } from '@/theme/colors';
import { RATE_UNAVAILABLE_LABEL, RateDisplayType, formatCredits } from '@/utils';

import AddToExpertRequestButton from './AddToExpertRequestButton';

const getStyles = makeStyles(() => ({
  sendMessageButton: {
    marginTop: 15,
    textAlign: 'center',
  },
}));

const getSharingStyles = makeStyles(() => ({
  sharing: {
    marginTop: 10,
    textAlign: 'center',
    fontSize: 12,
  },
  sharingGroup: {
    fontWeight: 'bold',
  },
}));

const getBookingStyles = makeStyles(() => ({
  credits: {
    textAlign: 'center',
  },
  creditsLabel: {
    textTransform: 'uppercase',
    fontSize: 10,
  },
  creditsValue: {
    fontWeight: 'bold',
    fontSize: 14,
  },
  expertStateButton: {
    marginTop: 10,
  },
  buttonInfo: {
    color: darkGray,
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
  },
}));

interface BookingProps {
  profile: NonNullable<Viewer['profile']>;
  requestUrl: string;
  onRequestConsultation?: () => void;
  canRequestConsultation?: boolean;
  requestConsultationDisabled?: boolean;
}

function Booking({
  profile,
  requestUrl,
  onRequestConsultation,
  canRequestConsultation,
  requestConsultationDisabled,
}: BookingProps): JSX.Element {
  const navigate = useNavigate();
  const s = getBookingStyles();
  const displayType = useRateDisplay();

  // USD only if client_rate exists, otherwise fall back to credits
  // Use client_rate (includes take rate) instead of bill_rate for SMB pricing
  const clientRate = profile.client_rate ?? 0;
  const showUSD = displayType === RateDisplayType.USD && clientRate > 0;

  // A null credit_rate means the API could not price this expert, not that they are
  // free — show that explicitly rather than rendering "0 credits/hr".
  const creditRate = profile.credit_rate ?? null;
  const rateKnown = showUSD || creditRate !== null;

  const rateValue = showUSD ? `$${Math.round(clientRate / 100)}` : formatCredits(creditRate ?? 0);
  const rateUnit = showUSD ? '/hr' : ` credit${(creditRate ?? 0) / 100 !== 1 ? 's' : ''}/hr`;

  const handleClick = onRequestConsultation || (() => navigate(requestUrl));

  return (
    <div>
      <Divider spacing={10} />

      <div className={s.credits}>
        {rateKnown ? (
          <>
            <span className={s.creditsLabel}>From</span>
            <span className={s.creditsValue}>
              &nbsp;{rateValue}
              {rateUnit}
            </span>
          </>
        ) : (
          <span className={s.creditsValue}>{RATE_UNAVAILABLE_LABEL}</span>
        )}
      </div>

      {canRequestConsultation && (
        <>
          <div className={s.expertStateButton}>
            <V2Tooltip
              title={
                requestConsultationDisabled
                  ? 'This expert request isn’t Active, so calls can’t be requested.'
                  : ''
              }
              placement="top"
              slotProps={{ tooltip: { sx: { whiteSpace: 'nowrap', maxWidth: 'none' } } }}
            >
              <span>
                <RequestConsultationButton
                  fullWidth
                  onClick={handleClick}
                  disabled={requestConsultationDisabled}
                />
              </span>
            </V2Tooltip>
          </div>

          <div className={s.buttonInfo}>You won&apos;t be charged until confirming</div>
        </>
      )}
    </div>
  );
}

function Sharing({ profile }: any) {
  const s = getSharingStyles();

  const { expert_internal_networks: inetexps } = profile;
  const groupNames = (inetexps || [])
    .map((ine: any) => ine.from_group && ine.from_group.name)
    .filter(Boolean);

  if (!groupNames.length) return null;
  if (groupNames.length === 1)
    return (
      <div className={s.sharing}>
        Shared by <span className={s.sharingGroup}>{groupNames[0]}</span>
      </div>
    );

  const tooltip = (
    <div>
      {groupNames.slice(1).map((gn: string) => (
        <div key={gn}>• {gn}</div>
      ))}
    </div>
  );

  const otherPluralized = groupNames.length === 2 ? 'other' : 'others';

  return (
    <div className={s.sharing}>
      Shared by <span className={s.sharingGroup}>{groupNames[0]}</span>
      &nbsp;and{' '}
      <Tooltip
        overlay={tooltip}
        trigger={['hover']}
        overlayStyle={{ zIndex: 2000 }}
        placement="bottom"
      >
        <span>
          {groupNames.length - 1} {otherPluralized}
        </span>
      </Tooltip>
    </div>
  );
}

export interface ActionsProps extends BookingProps {
  user: Viewer;
  showMessageButton?: boolean;
}

function Actions({
  canRequestConsultation,
  user,
  profile,
  requestUrl,
  onRequestConsultation,
  showMessageButton,
  requestConsultationDisabled,
}: ActionsProps): JSX.Element | null {
  const s = getStyles();

  const theme = useTheme();

  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  if (mobile) return null;

  return (
    <>
      <Booking
        canRequestConsultation={canRequestConsultation}
        profile={profile}
        requestUrl={requestUrl}
        onRequestConsultation={onRequestConsultation}
        requestConsultationDisabled={requestConsultationDisabled}
      />

      <AddToExpertRequestButton
        fullWidth
        profile={profile}
        style={{ marginTop: 15 }}
        form="addToExpertRequestFromProfile"
      />

      {/* Consulting engagements (ENG-5452). Two gates, both needed.

          `canRequestConsultation` is the same eligibility the Booking CTA above
          uses (`user.expert_state === 'active' && profile.can_request_consultation`).
          Without it this rendered on profiles nobody can engage — including the
          viewer's own, since `self` gates the message button in `Profile.tsx`
          but not this whole stack, which would have offered a customer a button
          to hire themselves.

          The component then self-gates on the account question, so it is a no-op
          for anyone outside the engagements allowlist. Same button props and the
          same `marginTop: 15` as the sibling above, because it is the same kind
          of affordance in the same stack. */}
      {canRequestConsultation && (
        <HireExpertButton
          fullWidth
          style={{ marginTop: 15 }}
          source={{
            creationPath: EngagementCreationPath.FromProfile,
            profileId: profile.id,
            // The drawer names the expert rather than re-reading them
            // (ENG-5734). This page already has the name it is rendering.
            expertName: profile.name ?? undefined,
          }}
        />
      )}

      {showMessageButton && (
        <div className={s.sendMessageButton}>
          <SendMessageButton userId={user.id} profileSlug={profile.url_endpoint} />
        </div>
      )}

      <Sharing profile={profile} />
    </>
  );
}

export default Actions;
